import { Box, Grid2, Skeleton, Typography } from '@mui/material'

import { Maybe, PostBoxFragment } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, SideMenuProps } from '@/components/layouts'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { CellFrame, Template } from '@/core/architecture'
import { individualDisplayResponsive } from '@/core/individual'
import { useSettingUserInfoModal } from '@/core/membership/useSettingUserInfoModal'

import { ShortVideo } from './ShortVideo'

export * from './fragments'

export type State = {
  posts: PostBoxFragment[]
  isMembershipPersonalInformationRequired: boolean
  loading: boolean
  refetching: boolean
  hasNextPage: boolean
  liveStreamId: string | undefined
  user?: Maybe<SideMenuProps['user']>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
}

export const ArtistPostVideosTemplate: Template<State, Action> = ({
  state,
  state: { liveStreamId, user },
  ...action
}) => {
  const { t, i18n } = useTranslation('artistPostVideos')

  const { router, routerPages, routerNames, generatePath } = useRouter<{
    artistId: string
  }>()

  const displayResponsive = individualDisplayResponsive()
  return (
    <ArtistThemeProvider>
      {/* LANAアプリで画面のタイトルを変更しているため、og:titleも合わせて変更する */}
      <Head
        title={
          i18n.exists('artistPostVideos:ogTitle')
            ? t('ogTitle')
            : routerNames.ArtistPostVideos
        }
      />
      <BaseLayout
        navigation={{
          type: 'artist',
          props: {
            hasManagementTask: !!user?.hasManagementTask,
            unreadNotificationCount: user?.unreadNotificationCount,
            isLive: !!liveStreamId,
          },
          responsive: displayResponsive,
        }}
        header={{
          title: t('title'),
          back: true,
          defaultBackPage: generatePath(routerPages.ArtistTop, {
            artistId:
              (config.applicationType === 'standalone'
                ? config.artistId
                : router.query.artistId) ?? '#',
          }),
          ...(displayResponsive ? { responsive: true, maxWidth: 1120 } : {}),
        }}
        maxWidth={displayResponsive ? 1120 : undefined}
      >
        <ShortVideoCell
          state={state}
          action={action}
          responsive={displayResponsive}
        />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

const ShortVideoCell = ({
  state: {
    posts,
    refetching,
    loading,
    hasNextPage,
    isMembershipPersonalInformationRequired,
  },
  action: { onFetchMore },
  responsive,
}: {
  state: State
  action: Action
  responsive: boolean
}) => {
  const { t } = useTranslation('artistPostVideos')
  const { router, routerPages, generatePath } = useRouter()
  const { showModal } = useSettingUserInfoModal()

  return (
    <CellFrame>
      <ScrollFetcher
        component={Grid2}
        componentProps={{
          container: true,
          spacing: 0,
        }}
        itemComponent={Grid2}
        itemComponentProps={
          responsive ? { size: { xs: 4, md: 3 } } : { size: { xs: 4 } }
        }
        fadeSx={{ aspectRatio: '9/16' }}
        nodes={posts.map((post, index) => (
          <ShortVideo
            key={index}
            post={post}
            onClick={() => {
              if (
                post.isAccessible &&
                post.isPremium &&
                isMembershipPersonalInformationRequired &&
                post.artist?.patron?.isPersonalInformationRequired
              ) {
                showModal()
              } else {
                router.push(
                  generatePath(routerPages.PostVideoDetailModal, {
                    postId: post.id,
                  })
                )
              }
            }}
          />
        ))}
        onFetchMore={onFetchMore}
        loading={refetching || loading}
        hasNextPage={hasNextPage}
        emptyMessage={<EmptyMessage message={t('postNotExist')} />}
        loadingComponent={
          <Grid2 container spacing={1} sx={{ width: '100%' }}>
            {Array.from(Array(9).keys()).map(key => (
              <Grid2 size={{ xs: 4 }} key={key}>
                <Skeleton
                  variant="rounded"
                  sx={{
                    width: '100%',
                    height: '100%',
                    minHeight: '30vh',
                  }}
                />
              </Grid2>
            ))}
          </Grid2>
        }
      />
    </CellFrame>
  )
}

const EmptyMessage = ({ message }: { message: string }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '85dvh',
      }}
    >
      <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
        {message}
      </Typography>
    </Box>
  )
}
